import { useEffect, useState, useCallback } from "react";

/**
 * useInstructionsVisibility — global preference (localStorage) for whether
 * the per-screen Instructions panels are visible.
 *
 * Returns: { visible, hide, show, toggle }
 *   - visible: boolean (default true on first visit)
 *   - hide():  set to false + persist
 *   - show():  set to true + persist
 *   - toggle(): flip + persist
 *
 * Cross-tab sync via the `storage` event so a change in one tab updates
 * the other.
 */
const STORAGE_KEY = "bls.showInstructions";
const EVENT_NAME = "bls:instructions-visibility";

function readInitial() {
  if (typeof window === "undefined") return true;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === null) return true; // default = visible
    return v === "true";
  } catch (err) {
    console.debug("instructions visibility read:", err);
    return true;
  }
}

export default function useInstructionsVisibility() {
  const [visible, setVisible] = useState(readInitial);

  const persist = useCallback((next) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch (err) {
      console.debug("instructions visibility persist:", err);
    }
    // notify other components in this tab
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
  }, []);

  const hide = useCallback(() => {
    setVisible(false);
    persist(false);
  }, [persist]);

  const show = useCallback(() => {
    setVisible(true);
    persist(true);
  }, [persist]);

  const toggle = useCallback(() => {
    setVisible((prev) => {
      const next = !prev;
      persist(next);
      return next;
    });
  }, [persist]);

  // Cross-tab + same-tab sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setVisible(e.newValue === "true");
    };
    const onCustom = (e) => setVisible(Boolean(e.detail));
    window.addEventListener("storage", onStorage);
    window.addEventListener(EVENT_NAME, onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(EVENT_NAME, onCustom);
    };
  }, []);

  return { visible, hide, show, toggle };
}
